import { useEffect } from "react";
/**
 * Hook to execute a callback when click outside of a given DOM
 * element (contentRef.current) is detected.
 *
 * @param {Ref} React.ref ref to a dom element
 * @param {Function} callback A function to execute on click outside
 * @param {Boolean} active A boolean indicating if this hook is active
 * @param {Object} an object containing the following signature: {triggerRef, type}
 * triggerRef: {Ref} React.ref ref to a trigger element.
 * type: {String} The type is used to find out whether it is balloon or not.
 */

export default function useOnClickOutside(contentRef, callback, active, triggerInfo) {
  useEffect(() => {
    if (active) {
      const onClick = event => {
        const {
          target
        } = event;
        const {
          triggerRef,
          type
        } = triggerInfo || {};
        const triggerNode = triggerRef === null || triggerRef === void 0 ? void 0 : triggerRef.current;
        const contentRefClick = contentRef.current.contains(target);
        /**
         * In case of Panel, we only want to trigger the callback when
         * the click is done on the overlay (the triggerRef).
         */

        if (type === "panel") {
          if (target === triggerNode) {
            return callback(event);
          } else {
            return;
          }
        }
        /**
         * The following condition will be used to differentiate the
         * document click vs toggle balloon by clicking trigger element.
         */


        if (type === "balloon") {
          if (!contentRefClick && triggerNode && !triggerNode.contains(target) && document.body.contains(target)) {
            if (triggerNode && triggerNode.containerRef && triggerNode.containerRef === target) {
              return;
            }

            return callback(event);
          }
        } else if (!contentRefClick) {
          return callback(event);
        }
      };

      document.addEventListener("click", onClick, {
        capture: true,
        passive: false
      });
      document.addEventListener("mousedown", onClick, {
        capture: true,
        passive: false
      });
      return () => {
        document.removeEventListener("click", onClick, {
          capture: true,
          passive: false
        });
        document.removeEventListener("mousedown", onClick, {
          capture: true,
          passive: false
        });
      };
    }
  }, [contentRef, callback, active, triggerInfo]);
}